/**
 * PreviewErrorBoundary — the Inbox preview pane's own failure surface.
 *
 * Why this exists. The CEO's standing instruction after the 2026-07-29 inbox
 * session: "When something cannot load, it must SAY SO on screen — never leave
 * stale content that looks like a successful render."
 *
 * Before this, the preview pane had NO boundary of its own. A detail renderer
 * that threw was caught only by AppErrorBoundary at the root of the whole
 * application, so one bad card took down the entire CEO Office — inbox list,
 * tabs and all — and the CEO had no way to tell which card did it or to move
 * on to the next one. There was no in-between: either the card rendered, or
 * the product disappeared.
 *
 * This boundary is scoped to ONE card. When a card cannot be shown, the pane
 * says so in plain words, names the card, and the rest of the inbox keeps
 * working. It is announced over aria-live because the CEO reads by screen
 * reader.
 *
 * `resetKey` is the selected card's id: changing card clears the error state,
 * so a single bad card does not poison the pane for every card after it.
 */

import React from 'react';
import { DS_COLORS, DS_TYPOGRAPHY } from '@/config/ui-invariants';
import { COCKPIT_LABELS } from '@/config/task-display-config';

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Loud in the console for a developer; plain words on screen for the CEO.
    console.error('[InboxPreview] card failed to render', error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;

    const { cardTitle } = this.props;

    return (
      <div
        data-testid="inbox-preview-failed"
        role="alert"
        aria-live="assertive"
        style={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          height: '100%',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <p
            style={{
              ...DS_TYPOGRAPHY.bodyMd,
              fontWeight: 700,
              color: DS_COLORS.text.error,
              margin: '0 0 8px',
            }}
          >
            {COCKPIT_LABELS.emptyState.previewFailedTitle}
          </p>
          <p
            style={{
              ...DS_TYPOGRAPHY.bodyMd,
              color: DS_COLORS.text.primary,
              margin: 0,
            }}
          >
            {cardTitle
              ? `${COCKPIT_LABELS.emptyState.previewFailedBody} (${cardTitle})`
              : COCKPIT_LABELS.emptyState.previewFailedBody}
          </p>
        </div>
      </div>
    );
  }
}

export default PreviewErrorBoundary;
